// The first run's tour: one real control at a time, spotlit, with a sentence
// beside it (components/ui/Coachmark). A `do` step moves on when the thing has
// happened, read from the app's own state like Get started's steps, never from
// a tap on the card; a `look` step has Got it. Anything others in the
// household would see (starting a trip shows them "Join …") is only ever a
// `look`. Every step has Skip tour.

import type { Layout } from './layout'
import type { ShopsWith } from './firstRun'

export type TourStepId = 'open-list' | 'add' | 'row' | 'shop' | 'invite' | 'recipes' | 'start'

export interface TourStep {
  id: TourStepId
  /** The element it points at: `[data-tour="…"]`. */
  anchor: string
  kind: 'do' | 'look'
  text: string
  layouts: readonly Layout[]
}

const BOTH = ['phone', 'desktop'] as const

export const TOUR: TourStep[] = [
  { id: 'open-list', anchor: 'home-list', kind: 'do', layouts: ['phone'],
    text: 'This is your list. Open it.' },
  { id: 'add', anchor: 'quick-add', kind: 'do', layouts: BOTH,
    text: 'Add something. The aisle picks itself.' },
  { id: 'row', anchor: 'list-row', kind: 'look', layouts: BOTH,
    text: 'Tap an item to change it. Swipe it left to delete it, and Undo brings it back.' },
  { id: 'shop', anchor: 'start-shopping', kind: 'look', layouts: ['phone'],
    text: 'In the store, start here. The list follows the store’s aisles and learns the way you walk them.' },
  { id: 'invite', anchor: 'people', kind: 'look', layouts: BOTH,
    text: 'Invite the others here. What they add shows up on your phone as they type it.' },
  { id: 'recipes', anchor: 'tab-recipes', kind: 'look', layouts: BOTH,
    text: 'Paste a recipe link. Its ingredients land in your aisles.' },
  { id: 'start', anchor: 'get-started', kind: 'look', layouts: BOTH,
    text: 'What’s left to set up is in here. That’s the tour.' },
]

export interface TourInput {
  layout: Layout
  shopsWith?: ShopsWith
  joined?: boolean
  /** Get started's button is showing. */
  getStarted: boolean
}

export function stepsFor({ layout, shopsWith, joined, getStarted }: TourInput): TourStep[] {
  return TOUR.filter(s => s.layouts.includes(layout)
    && !(s.id === 'invite' && (shopsWith === 'me' || joined))
    && !(s.id === 'start' && !getStarted))
}

/** What the tour reads from the app. */
export interface TourSnapshot { tab: string; items: number }

/** A `do` step is done once it happened since the step began (`begun`); a `look` step only by Got it. */
export function stepDone(step: TourStep, now: TourSnapshot, begun: TourSnapshot): boolean {
  if (step.id === 'open-list') return now.tab === 'list'
  if (step.id === 'add') return now.items > begun.items
  return false
}

export function currentStep(steps: TourStep[], seen: readonly string[]): TourStep | null {
  return steps.find(s => !seen.includes(s.id)) ?? null
}
